import type { Step, VideoInfo } from './types';

/**
 * YouTube embed URL for a chaptered video guide, with the JS API enabled so the
 * persistent player can be seeked as the reader navigates. Parity with
 * getVideoEmbedUrl() in embed.v5.steps.js.
 */
export function getVideoEmbedUrl(videoInfo: VideoInfo | null | undefined): string {
   if (!videoInfo || videoInfo.type !== 'youtube' || !videoInfo.videoId) {
      return '';
   }
   const params = new URLSearchParams({
      enablejsapi: '1',
      origin: window.location.origin,
      rel: '0',
      modestbranding: '1',
      playsinline: '1',
   });
   return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoInfo.videoId)}?${params.toString()}`;
}

const MIME_BY_EXTENSION: Record<string, string> = {
   mp4: 'video/mp4',
   m4v: 'video/mp4',
   webm: 'video/webm',
   ogv: 'video/ogg',
   ogg: 'video/ogg',
   mov: 'video/quicktime',
};

function mimeForUrl(url: string): string {
   const extension = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase() ?? '';
   return MIME_BY_EXTENSION[extension] ?? 'video/mp4';
}

/**
 * <source> list for an iFixit self-hosted chaptered video guide. Derives each
 * MIME from its URL extension so any rendition in `videoUrls` is included —
 * the vanilla getVideoSources() hardcoded a `{mp4_720: 'video/mp4', …}` key
 * allowlist that silently dropped new renditions (iFixit/ifixit#62872).
 */
export function getVideoSources(
   videoInfo: VideoInfo | null | undefined
): { url: string; mime: string }[] {
   const videoUrls = (videoInfo as { videoUrls?: Record<string, string> } | null)?.videoUrls;
   if (!videoInfo || videoInfo.type !== 'ifixit' || !videoUrls) {
      return [];
   }
   return Object.values(videoUrls)
      .filter(Boolean)
      .map(url => ({ url, mime: mimeForUrl(url) }));
}

/**
 * The step whose video chapter the playhead is in: the latest step whose
 * timestamp starts at or before `time`. Ranges can overlap or nest, so a
 * "first range containing time" lookup would wrongly stick on the earlier step
 * (see getStepNumberForTime() in embed.v5.steps.js).
 */
export function getStepNumberForTime(steps: Step[], time: number): number | null {
   let current: Step | null = null;
   for (const step of steps) {
      const ts = step.videoTimestamp;
      if (
         ts &&
         time >= ts.startTime &&
         (!current || ts.startTime > current.videoTimestamp!.startTime)
      ) {
         current = step;
      }
   }
   return current?.number ?? null;
}

export function isTimestampedVideoStep(
   guide: { isVideoGuide?: boolean; steps: Step[] },
   stepNumber: number
): boolean {
   return Boolean(
      guide.isVideoGuide &&
         stepNumber > 0 &&
         stepNumber <= guide.steps.length &&
         guide.steps[stepNumber - 1].videoTimestamp
   );
}

let youtubeApiPromise: Promise<void> | null = null;

// Load the YouTube IFrame API once, resolving when YT.Player is available.
// Mirrors loadYoutubeApi() in embed.v5.steps.js.
export function loadYoutubeApi(): Promise<void> {
   const yt = (window as unknown as { YT?: { Player?: unknown } }).YT;
   if (yt?.Player) {
      return Promise.resolve();
   }
   if (youtubeApiPromise) {
      return youtubeApiPromise;
   }
   youtubeApiPromise = new Promise<void>(resolve => {
      const w = window as unknown as { onYouTubeIframeAPIReady?: () => void };
      const previous = w.onYouTubeIframeAPIReady;
      w.onYouTubeIframeAPIReady = () => {
         previous?.();
         resolve();
      };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.head.append(script);
   });
   return youtubeApiPromise;
}
